import { SessionHelper } from "app/helpers/sessionHelper";
import { EventStatus } from "app/utils/const";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import IncidentsHelper from "../helpers/incidentsHelper";
import store from "../store/store";
import type { CompetitionType } from "../types/competitionType";
import type {
  EventIncidentType,
  EventMarketPeriodType,
  EventMarketWSType,
  EventRunnerType,
  EventRunnerWSType,
  EventSummaryWSType,
  EventType,
} from "../types/eventType";
export default class CompetitionLogic {
  static updateCompetitionEvent(competitionEventUpdates: EventType[]) {
    let competitionList: CompetitionType[] = cloneDeep(
      store.getState().competitionListModel.competitionList
    );

    if (competitionList.length > 0) {
      competitionEventUpdates.forEach(competitionEventUpdate => {
        // try and find competition in competition list
        let competition = competitionList.find(
          competition =>
            competition.id === competitionEventUpdate.competition.id
        );
        // try and find event in competition
        let event = null;

        if (competition !== undefined) {
          event = competition.events.find(
            e => e.id === competitionEventUpdate.id
          );
        }

        // try and update event in selected competition in competitionList
        if (competition !== null && event !== null) {
          updateEventData(event, competitionEventUpdate);
          store.dispatch.competitionListModel.updateEventInCompetitionInList(
            event
          );
        }

        if (
          !SessionHelper.isAsianRouteAndSessionView() &&
          !isEmpty(
            store.getState().competitionListModel.unsummarizedMarketsModalEvent
          ) &&
          store.getState().competitionListModel.unsummarizedMarketsModalEvent &&
          store.getState().competitionListModel.unsummarizedMarketsModalEvent
            .id === event.id &&
          event.status === EventStatus.CLOSED
        ) {
          store.dispatch.competitionListModel.updateUnsummarizedMarketsModalEvent(
            null
          );
        }
      });
    }
  }

  static updateMarket(marketUpdates: EventMarketWSType[]) {
    let competitionList: CompetitionType[] = cloneDeep(
      store.getState().competitionListModel.competitionList
    );

    if (competitionList.length > 0) {
      // waiting on API update with competition id;
      marketUpdates.forEach(marketUpdate => {
        // Find the event to be updated
        // try and find competition from competition list using eventId
        let competition = competitionList.find(competition =>
          competition.events.find(event => event.id === marketUpdate["eventId"])
        );
        let event = null;

        // try and find event is in competition
        if (!isEmpty(competition)) {
          event =
            competition &&
            competition.events.find(
              event => event.id === marketUpdate["eventId"]
            );
        }

        //
        // find market to be updated
        let foundMarketPeriod;

        if (event != null && event.marketPeriods) {
          //  european view data structure
          foundMarketPeriod = event.marketPeriods.find(
            mp => mp.marketPeriod === marketUpdate.period
          );
        }

        if (foundMarketPeriod != null) {
          let foundMarketType = foundMarketPeriod.marketTypes.find(
            mt => mt.marketType === marketUpdate.type
          );

          if (foundMarketType != null) {
            let foundMarket = foundMarketType.markets.find(
              m => m.id === marketUpdate.id
            );

            if (foundMarket != null) {
              foundMarket.status = marketUpdate.status;
              //update competition competitionListModel with WS market update
              store.dispatch.competitionListModel.updateEventInCompetitionInList(
                event
              );
            }
          }
        } //
      });
    }
  }

  static updateRunner(runnerUpdatesWS: EventRunnerWSType[]) {
    let competitionList: CompetitionType[] = cloneDeep(
      store.getState().competitionListModel.competitionList
    );

    if (competitionList.length > 0) {
      // Update the competition list with runner status update
      runnerUpdatesWS.forEach(runnerUpdateWS => {
        // try and find competition from competition list using eventId
        let competition = competitionList.find(compeition =>
          compeition.events.find(
            event => event.id === runnerUpdateWS["eventId"]
          )
        );
        let event = null;

        if (!isEmpty(competition)) {
          // try and find event is in competition
          event =
            competition &&
            competition.events.find(
              event => event.id === runnerUpdateWS["eventId"]
            );
        }

        // update competition event  with runner updates
        if (event != null) {
          let marketDetails;

          if (event.marketPeriods != null) {
            marketDetails = getMarketDetails(
              event.marketPeriods,
              runnerUpdateWS
            );
          }

          let foundMarketPeriod = event.marketPeriods.find(
            mp => mp.marketPeriod === marketDetails.marketPeriod
          );

          if (foundMarketPeriod != null) {
            let foundMarketType = foundMarketPeriod.marketTypes.find(
              mt => mt.marketType === marketDetails.marketType
            );

            if (foundMarketType != null) {
              let foundMarket = foundMarketType.markets.find(
                m => m.id === runnerUpdateWS["marketId"]
              );

              if (foundMarket != null) {
                let foundRunner = foundMarket.runners.find(
                  r => r.id === runnerUpdateWS.id
                );

                if (foundRunner != null) {
                  foundRunner.status = runnerUpdateWS.status;
                  // update competition event in competitionListModel with WS runner update
                  store.dispatch.competitionListModel.updateEventInCompetitionInList(
                    event
                  );
                }
              }
            }
          }
        }
      });
    }
  }

  // PNL for exchange markets
  // Updated after a bet is placed
  // Update comes from poll or websockets, (same as bet status)
  static updateRunnerPNL(
    runnersPNL: { runnerId: number; runnerProfitAndLoss: number }[]
  ) {
    let unsummarizedMarketsModalEvent = cloneDeep(
      store.getState().competitionListModel.unsummarizedMarketsModalEvent
    );

    runnersPNL.forEach(rpnl => {
      const runner = findRunnerById(
        unsummarizedMarketsModalEvent,
        rpnl.runnerId
      );
      if (runner) {
        runner.runnerProfitAndLoss = rpnl.runnerProfitAndLoss;
      }
    });
    store.dispatch.competitionListModel.updateUnsummarizedMarketsModalEvent(
      unsummarizedMarketsModalEvent
    );
  }

  /*
   ** INTERNAL METHODS
   */
  static updateEventIncident(eventIncidentUpdatesWS: EventIncidentType[]) {
    let competitionList: CompetitionType[] = cloneDeep(
      store.getState().competitionListModel.competitionList
    );
    // Update the compeition list with event incident WS update
    competitionList.length > 0 &&
      eventIncidentUpdatesWS.forEach(eventIncidentUpdateWS => {
        // find the competition
        let competition =
          competitionList.length > 0 &&
          competitionList.find(competition =>
            competition.events.find(event => {
              return event.id === eventIncidentUpdateWS["eventId"];
            })
          );
        // find the event
        let event = null;

        if (competition && competition !== undefined) {
          event = competition.events.find(
            event => event.id === eventIncidentUpdateWS["eventId"]
          );
        }

        if (event != null && event["eventIncidents"] != null) {
          IncidentsHelper.updateEventIncidentData(event, eventIncidentUpdateWS);
          store.dispatch.competitionListModel.updateEventInCompetitionInList(
            event
          );
        }

        // Update event modal/detail screen
        if (
          store.getState().competitionListModel.unsummarizedMarketsModalEvent &&
          store.getState().competitionListModel.unsummarizedMarketsModalEvent
            .id === eventIncidentUpdateWS["eventId"]
        ) {
          let modalEvent: EventType = cloneDeep(
            store.getState().competitionListModel.unsummarizedMarketsModalEvent
          );
          IncidentsHelper.updateEventIncidentData(
            modalEvent,
            eventIncidentUpdateWS
          );
          store.dispatch.competitionListModel.updateUnsummarizedMarketsModalEvent(
            modalEvent
          );
        }
      });
  }

  static updateNumberLiveEvents(EventSummaryWS: EventSummaryWSType) {
    store.dispatch.competitionListModel.updateCompetitionListNumberLiveEvents(
      EventSummaryWS.currentNumberOfLiveEvents
    );
  }
} // update event data

function updateEventData(event: EventType, eventUpdate: EventType) {
  event["allowLiveBetting"] = eventUpdate["allowLiveBetting"];
  event["inRunning"] = eventUpdate["inRunning"];
  // BET-7213
  // ws event notification is returning incorrect numberOfMarkets.
  // This will take time to fix, so in the mean time, rely on event polling, which is returning the correct numberOfMarkets
  // comment the following line until api changes are made
  // event["numberOfMarkets"] = eventUpdate["numberOfMarkets"];
  //
  event["startTime"] = eventUpdate["startTime"];
  event.status = eventUpdate.status;
}

// find marketPeriod and marketType to find correct market for runner
function getMarketDetails(
  marketPeriods: EventMarketPeriodType[],
  runnerUpdate: EventRunnerWSType
) {
  const marketDetails = {
    marketPeriod: "",
    marketType: "",
  };
  marketPeriods.forEach(marketPeriod => {
    marketPeriod.marketTypes.forEach(marketType => {
      marketType.markets.forEach(market => {
        // check if market matches runner market id
        if (market.id === runnerUpdate["marketId"]) {
          marketDetails.marketPeriod = marketPeriod.marketPeriod;
          marketDetails.marketType = marketType.marketType;
        }
      });
    });
  });
  return marketDetails;
}
// Finds and returns a runner in an event
function findRunnerById(
  eventData: EventType,
  runnerId: number
): EventRunnerType | null {
  if (
    !eventData ||
    !eventData.marketPeriods ||
    eventData.marketPeriods.length === 0
  ) {
    return null;
  }

  for (let marketPeriod of eventData.marketPeriods) {
    for (let marketType of marketPeriod.marketTypes) {
      for (let market of marketType.markets) {
        for (let runner of market.runners) {
          if (runner.id === runnerId) {
            return runner;
          }
        }
      }
    }
  }

  return null;
}
